import classNames from 'classnames';
import ImageUploader, { MEDIA_TYPES_IMAGES } from 'components/ImageUploader';
import Spinner from 'components/spinner/Spinner';
import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';
import { ENTERTAINMENT_VIDEO_SUPPORTED_FILE_TYPES } from 'modules/entertainment-videos/constants';
import RealmOutfitImageUploadPlaceholder from 'modules/realm-outfits/components/realm-outfit-image-upload-placeholder/RealmOutfitImageUploadPlaceholder';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { danger } from 'redux-store/reducers/common-reducer';
import { __t, applyTranslations } from 'utils/language';
import './entertainment-video-media.scss';
import { entertainmentVideoMaxDurationSelector, entertainmentVideoMaxFileSizeSelector } from './selectors';
import { bytesToMegabytes } from './helpers';
import { validateVideoDuration } from 'utils/video';

applyTranslations(require('./entertainment-video-media.i18n.json'));

function translate(path: string) {
  return __t(`entertainmentVideoMedia${path}`);
}

type OnUploadVideo = (file: File) => void;
type OnDeleteThumbnail = () => void;
type OnUploadThumbnail = (file: File) => void;

export function EntertainmentVideoMedia({
  videoUrl,
  thumbnailUrl,
  isEditMode = false,
  isUploading = false,
  onUploadVideo,
  onDeleteThumbnail,
  onUploadThumbnail
}: {
  videoUrl: string | null,
  thumbnailUrl: string | null,
  isEditMode?: boolean,
  isUploading?: boolean,
  onUploadVideo?: OnUploadVideo,
  onDeleteThumbnail?: OnDeleteThumbnail,
  onUploadThumbnail?: OnUploadThumbnail
}) {
  return (
    <div style={{ display: 'flex', gap: 24 }}>
      {isUploading && <Spinner />}
      {/* eslint-disable-next-line @typescript-eslint/no-use-before-define */}
      <Video
        isEditMode={isEditMode}
        url={videoUrl}
        onUploadVideo={onUploadVideo}
      />
      {/* eslint-disable-next-line @typescript-eslint/no-use-before-define */}
      <Thumbnail
        isEditMode={isEditMode}
        thumbnailUrl={thumbnailUrl}
        onDeleteThumbnail={onDeleteThumbnail}
        onUploadThumbnail={onUploadThumbnail}
      />
    </div>
  );
}

function Video({
  isEditMode,
  url,
  onUploadVideo
}: {
  isEditMode: boolean,
  url: string | null,
  onUploadVideo?: (file: File) => void
}) {
  const dispatch = useDispatch();
  const entertainmentVideoMaxFileSize = useSelector(entertainmentVideoMaxFileSizeSelector);
  const entertainmentVideoMaxDuration = useSelector(entertainmentVideoMaxDurationSelector);
  const [isValidating, setIsValidating] = useState(false);

  async function validateVideo(file: File): Promise<boolean> {
    if (file.size > entertainmentVideoMaxFileSize) {
      dispatch(
        danger(`${translate('.maxFileSizeExceeded')} ${bytesToMegabytes(entertainmentVideoMaxFileSize).toFixed(0)} ${translate('.units.megabytes')}.`)
      );

      return false;
    }

    try {
      const isDurationValid = await validateVideoDuration(file, entertainmentVideoMaxDuration);

      if (!isDurationValid) {
        dispatch(
          danger(`${translate('.maxDurationExceeded')} ${entertainmentVideoMaxDuration} ${translate('.units.seconds')}.`)
        );

        return false;
      }
    } catch (err) {
      dispatch(
        danger(translate('.durationValidationError'))
      );

      return false;
    }

    return true;
  }

  return (
    <React.Fragment>
      <div className="entertainment-video-media">
        <p className="entertainment-video-media__label">
          {translate('.labels.entertainmentVideo')}
        </p>
        <div className="entertainment-video-media__card">
          <div className="entertainment-video-media__container">
            {url !== null ? (
              <video key={url} className="entertainment-video-media__video" controls>
                <track kind="captions" />
                <source src={url} />
                {translate('.videoNotSupported')}
              </video>
            ) : (
              <p className="entertainment-video-media__fallback-text">
                {translate('.noVideo')}
              </p>
            )}
          </div>
          {/* eslint-disable-next-line @typescript-eslint/no-use-before-define */}
          <MediaLink url={url} />
        </div>
      </div>
      {isEditMode && (
        <div className="entertainment-video-media">
          <p className="entertainment-video-media__label entertainment-video-media__label--muted">
            {translate('.uploadVideo')}
          </p>
          <label
            htmlFor="entertainment-video-upload"
            className="entertainment-video-upload"
          >
            <input
              type="file"
              id="entertainment-video-upload"
              className="entertainment-video-upload__input"
              accept={ENTERTAINMENT_VIDEO_SUPPORTED_FILE_TYPES.join(',')}
              onChange={async (e) => {
                const file = e.currentTarget.files?.[0];

                if (isValidating || !file) {
                  return;
                }

                setIsValidating(true);

                try {
                  const isValid = await validateVideo(file);

                  if (isValid) {
                    onUploadVideo?.(file);
                  }
                } catch {
                  dispatch(
                    danger(translate('.genericValidationError'))
                  );
                } finally {
                  setIsValidating(false);
                  // this fixes a bug when you cannot select the same file twice
                  e.target.value = '';
                }
              }}
            />
            <SVGDynamicIcon
              name="plus-inside-circle-custom-bg-white-fg"
              width={25}
              height={25}
              className="entertainment-video-upload__icon"
            />
          </label>
        </div>
      )}
    </React.Fragment>
  );
}

function Thumbnail({
  isEditMode,
  thumbnailUrl,
  onDeleteThumbnail,
  onUploadThumbnail
}: {
  isEditMode: boolean,
  thumbnailUrl: string | null,
  onDeleteThumbnail?: OnDeleteThumbnail,
  onUploadThumbnail?: OnUploadThumbnail
}) {
  return (
    <React.Fragment>
      <div className="entertainment-video-media">
        <p className="entertainment-video-media__label entertainment-video-media__label--muted">
          {translate('.labels.thumbnail')}
        </p>
        <div className="entertainment-video-media__card">
          <div className="entertainment-video-media__container">
            {thumbnailUrl ? (
              <img
                className="entertainment-video-media__thumbnail"
                src={thumbnailUrl}
                alt="thumbnail"
              />
            ) : (
              <p className="entertainment-video-media__fallback-text">
                {translate('.noThumbnail')}
              </p>
            )}
          </div>
          {/* eslint-disable-next-line @typescript-eslint/no-use-before-define */}
          <MediaLink url={thumbnailUrl} />
          {isEditMode && typeof thumbnailUrl === 'string' && (
            <div className="entertainment-video-media__overlay">
              <button
                type="button"
                className="entertainment-video-media__delete-btn"
                onClick={onDeleteThumbnail}
              >
                <SVGDynamicIcon name="trash-custom-fg" width={20} height={20} />
              </button>
            </div>
          )}
        </div>
      </div>
      {isEditMode && (
        <div className="entertainment-video-media">
          <p className="entertainment-video-media__label entertainment-video-media__label--muted">
            {translate('.uploadThumbnail')}
          </p>
          <ImageUploader
            key="entertainment-video-thumbnail-uploader"
            multiple={false}
            mediaTypes={MEDIA_TYPES_IMAGES}
            onUpload={onUploadThumbnail}
          >
            <RealmOutfitImageUploadPlaceholder mode="create" type="outfit" />
          </ImageUploader>
        </div>
      )}
    </React.Fragment>
  );
}

function MediaLink({ url }: { url?: string | null }) {
  const hasUrl = typeof url === 'string';

  return (
    <a
      className={classNames(
        'entertainment-video-media__link',
        !hasUrl && 'entertainment-video-media__link--disabled'
      )}
      rel="noopener noreferrer"
      target="_blank"
      href={hasUrl ? url : undefined}
    >
      {translate('.openInNewTab')}
    </a>
  );
}
